import type { WebViewBridge } from '@frontapp/plugin-sdk/dist/webViewSdkTypes';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { useStickySearchParams } from '~/providers/StickySearchParams';
import Script from '../Script';

declare global {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Front?: WebViewBridge;
  }
}

/**
 * Corso Concierge integration component.
 * Adds the Front App plugin SDK script and sets up context handling for the concierge functionality.
 * Sets up the necessary search parameters based on the Front App context.
 *
 * @see https://dev.frontapp.com/docs/plugin-getting-started
 */
// the merchant UI is loaded within an iframe in the Front App sidebar, but is kept running in the background when the sidebar is closed
export default function CorsoConcierge() {
  const [onUnmount, setOnUnmount] = useState<() => void>();
  useEffect(() => onUnmount, [onUnmount]);

  const navigate = useNavigate();

  // the Front subscription outlives any render, so it reads the latest sticky search through a ref
  const stickySearch = useStickySearchParams();
  const stickySearchRef = useRef(stickySearch);
  stickySearchRef.current = stickySearch;

  return (
    <Script
      src="https://dl.frontapp.com/libs/plugin-sdk-1.8.1.min.js"
      onLoad={() => {
        if (!window.Front) return; // TODO consider warning or error handling if it's missing
        const subscription = window.Front.contextUpdates.subscribe(
          (newContext) => {
            match(newContext).with({ type: 'singleConversation' }, (ctx) => {
              const storeId = ctx.conversation.tags.find(
                (tag) => tag.parentTag?.name === 'store_id',
              )?.name;

              // TODO get claim ID from a conversation tag if available instead of subject line
              const claimId = ctx.conversation.subject?.match(
                /Your claim request #(?<claimId>\d+)/,
              )?.groups?.claimId;

              const pathname = match({ storeId, claimId })
                .with(
                  { storeId: P.nonNullable, claimId: P.nonNullable },
                  (ids) => `/${ids.storeId}/claims/shipping/${ids.claimId}`, // ! ASSUMED TO ALWAYS BE A SHIPPING CLAIM
                )
                .with({ storeId: P.nonNullable }, (ids) => `/${ids.storeId}`)
                .otherwise(() => window.location.pathname); // generally just `/`

              // sticky search retains every other param, so only the ones that changed need to be in the URL
              const search = new URLSearchParams(
                Object.entries({
                  // `auth_secret` should already exist as it is provided by Front
                  ref: 'corso-concierge',
                  email: ctx.teammate.email,
                }).filter(
                  ([key, value]) => stickySearchRef.current.get(key) !== value,
                ),
              );

              const pathnameChanged = pathname !== window.location.pathname;
              const searchChanged = search.size > 0;
              if (!(pathnameChanged || searchChanged)) return;

              navigate({ pathname, search: search.toString() });
            });
          },
        );
        setOnUnmount(() => () => subscription.unsubscribe());
      }}
    />
  );
}
